import { FIELD_REGISTRY, isKnownField, operatorsFor, } from './fields.js';
/**
 * Appendix X.1, conditions. The evaluable half of the rule expression language.
 *
 * > One small language, used by promotions (§6.25), commission (§6.24), price rule conditions
 * > (Appendix V), lab routing (§6.8 `JOB-008`) and recall rules (§6.14). **Not a scripting language,
 * > a data structure**, evaluable identically on server and client, with no arbitrary code.
 *
 * X.4's safety rules are properties of the shape rather than of the interpreter, and they hold here
 * because there is nothing else the shape can express:
 *
 * - **No loops, no function calls, no user-supplied code.** A condition is a tree of comparisons.
 * - **Evaluation is bounded and cannot fail to terminate.** The tree is finite and acyclic by
 *   construction, since it is parsed from JSON and JSON cannot cycle. Depth is capped anyway,
 *   because a rule saved by a broken importer should be refused rather than overflow a stack.
 * - **Rules are validated on save against the field registry and the effect schema**, which is why
 *   `validateCondition` exists separately from `evaluateCondition` and why the evaluator refuses to
 *   run an unvalidated tree.
 */
export class ExpressionError extends Error {
    code;
    path;
    constructor(code, message, 
    /** Where in the tree, so a rule author can find it. */
    path = '') {
        super(path === '' ? message : `${message} (at ${path})`);
        this.code = code;
        this.path = path;
        this.name = 'ExpressionError';
    }
}
/**
 * Appendix X.4: "Evaluation is bounded and cannot fail to terminate."
 *
 * JSON cannot express a cycle, so the tree is already finite. The cap is against a pathological
 * import rather than against a loop: a thousand-deep nest would overflow the stack before it
 * finished, and refusing it at validation is better than a crash at the till.
 */
const MAX_DEPTH = 32;
// ---------------------------------------------------------------------------------------------
// Validation, X.4
// ---------------------------------------------------------------------------------------------
function isRecord(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
/**
 * Validate a condition against the field registry.
 *
 * Throws rather than returning a result, because X.4 puts this at save time and a rule that does not
 * validate must not be stored at all. The message names the field and the path, because the person
 * reading it is a shopkeeper writing a promotion.
 */
export function validateCondition(condition, path = '$') {
    validateAtDepth(condition, path, 0);
}
function validateAtDepth(condition, path, depth) {
    if (depth > MAX_DEPTH) {
        throw new ExpressionError('EXPRESSION_TOO_DEEP', `A condition may nest at most ${String(MAX_DEPTH)} deep`, path);
    }
    if (!isRecord(condition)) {
        throw new ExpressionError('EXPRESSION_MALFORMED', 'A condition must be an object', path);
    }
    const combinators = ['all', 'any', 'none'].filter((k) => Object.hasOwn(condition, k));
    const isComparison = Object.hasOwn(condition, 'field');
    if (combinators.length > 1) {
        throw new ExpressionError('EXPRESSION_MALFORMED', `A node carries more than one combinator (${combinators.join(', ')}). Nest them instead.`, path);
    }
    if (combinators.length === 1 && isComparison) {
        throw new ExpressionError('EXPRESSION_MALFORMED', 'A node is either a combinator or a comparison, never both', path);
    }
    if (combinators.length === 1) {
        const key = combinators[0];
        const children = condition[key];
        if (!Array.isArray(children)) {
            throw new ExpressionError('EXPRESSION_MALFORMED', `'${key}' must be an array`, path);
        }
        if (children.length === 0) {
            // An empty `all` is vacuously true and an empty `any` vacuously false, which are two different
            // silent behaviours for the same typo. A promotion that applies to everybody because its
            // condition list came back empty from an importer is the failure this prevents.
            throw new ExpressionError('EXPRESSION_EMPTY_COMBINATOR', `'${key}' has no conditions. An empty combinator is silently true or false depending on which one it is, so it is refused rather than guessed.`, path);
        }
        children.forEach((child, i) => {
            validateAtDepth(child, `${path}.${key}[${String(i)}]`, depth + 1);
        });
        return;
    }
    if (!isComparison) {
        throw new ExpressionError('EXPRESSION_MALFORMED', "A condition must have 'field' or one of 'all', 'any', 'none'", path);
    }
    validateComparison(condition, path);
}
function validateComparison(node, path) {
    const field = node['field'];
    if (typeof field !== 'string') {
        throw new ExpressionError('EXPRESSION_MALFORMED', "'field' must be a string", path);
    }
    // X.1: "the only fields that exist. An unknown field is a validation error, not null."
    if (!isKnownField(field)) {
        throw new ExpressionError('EXPRESSION_UNKNOWN_FIELD', `${field} is not a field. A rule referencing a field that does not exist would silently never match, which is indistinguishable from a promotion nobody redeemed.`, path);
    }
    const op = node['op'];
    const definition = FIELD_REGISTRY[field];
    const allowed = operatorsFor(definition.type);
    if (typeof op !== 'string' || !allowed.includes(op)) {
        throw new ExpressionError('EXPRESSION_BAD_OPERATOR', `'${String(op)}' is not valid on ${field}, which is a ${definition.type}. Allowed: ${allowed.join(', ')}.`, path);
    }
    validateValue(op, node['value'], definition.type, field, path);
}
function validateValue(op, value, type, field, path) {
    if (op === 'exists') {
        // `exists` takes no operand. A value here means the author expected something else to happen.
        if (value !== undefined) {
            throw new ExpressionError('EXPRESSION_MALFORMED', `'exists' takes no value, and ${field} was given one`, path);
        }
        return;
    }
    if (value === undefined) {
        throw new ExpressionError('EXPRESSION_MALFORMED', `'${op}' on ${field} needs a value`, path);
    }
    if (op === 'in' || op === 'not_in') {
        if (!Array.isArray(value) || value.length === 0) {
            throw new ExpressionError('EXPRESSION_MALFORMED', `'${op}' on ${field} needs a non-empty array. An empty one matches nothing, silently.`, path);
        }
        return;
    }
    if (op === 'between') {
        if (!Array.isArray(value) || value.length !== 2) {
            throw new ExpressionError('EXPRESSION_MALFORMED', `'between' on ${field} needs exactly two values`, path);
        }
        const [lo, hi] = value;
        if (typeof lo !== 'number' || typeof hi !== 'number') {
            throw new ExpressionError('EXPRESSION_MALFORMED', `'between' bounds must be numbers`, path);
        }
        if (lo > hi) {
            throw new ExpressionError('EXPRESSION_MALFORMED', `'between' on ${field} runs from ${String(lo)} down to ${String(hi)}, which matches nothing`, path);
        }
        return;
    }
    // A scalar comparison. The value has to be the field's own type, or the comparison is one of the
    // silent-never-matches shapes: '5' against 5 is false forever and looks like a rule nobody hits.
    const wrong = () => {
        throw new ExpressionError('EXPRESSION_TYPE_MISMATCH', `${field} is a ${type} and was compared with ${JSON.stringify(value)}`, path);
    };
    switch (type) {
        case 'money':
        case 'number':
            if (typeof value !== 'number' && typeof value !== 'bigint')
                wrong();
            return;
        case 'boolean':
            if (typeof value !== 'boolean')
                wrong();
            return;
        case 'string':
        case 'date':
            if (typeof value !== 'string')
                wrong();
            return;
        case 'string_list':
            // Only `in` and `not_in` reach here, and both were handled above.
            wrong();
    }
}
/**
 * Evaluate a validated condition.
 *
 * **Validates first, every time.** X.4 puts validation at save, and doing it again here costs a tree
 * walk and closes the case where a rule reached the database before this code existed, or through a
 * migration, or by hand. The alternative is an unknown field evaluating to `undefined` and the rule
 * silently never matching, which X.1 explicitly calls a validation error rather than null.
 */
export function evaluateCondition(condition, facts) {
    validateCondition(condition);
    return evaluateNode(condition, facts);
}
function evaluateNode(condition, facts) {
    if ('all' in condition)
        return condition.all.every((c) => evaluateNode(c, facts));
    if ('any' in condition)
        return condition.any.some((c) => evaluateNode(c, facts));
    if ('none' in condition)
        return !condition.none.some((c) => evaluateNode(c, facts));
    return evaluateComparison(condition, facts);
}
function evaluateComparison(node, facts) {
    const actual = facts[node.field];
    if (node.op === 'exists')
        return actual !== undefined && actual !== null;
    // A fact that is absent is not a match for any comparison. Treating it as null and comparing
    // would make `customer.last_purchase_days < 30` true for a customer who has never bought
    // anything, which is the opposite of what a win-back promotion means.
    if (actual === undefined || actual === null)
        return false;
    const expected = node.value;
    switch (node.op) {
        case '=':
            return sameValue(actual, expected);
        case '!=':
            return !sameValue(actual, expected);
        case '<':
            return compareNumeric(actual, expected) < 0;
        case '<=':
            return compareNumeric(actual, expected) <= 0;
        case '>':
            return compareNumeric(actual, expected) > 0;
        case '>=':
            return compareNumeric(actual, expected) >= 0;
        case 'between': {
            const [lo, hi] = expected;
            return compareNumeric(actual, lo) >= 0 && compareNumeric(actual, hi) <= 0;
        }
        case 'in':
            return membership(actual, expected);
        case 'not_in':
            return !membership(actual, expected);
        case 'matches':
            // X.4 forbids user-supplied code, so this is not a regular expression. It is a
            // case-insensitive substring, which is what a rule author writing `matches` at a counter
            // means, and it cannot backtrack, hang, or be crafted into one that does.
            return String(actual).toLowerCase().includes(String(expected).toLowerCase());
    }
}
/**
 * Equality that treats a number and a bigint of the same value as equal.
 *
 * Money fields are minor units and arrive as `bigint` from the database and as `number` from a rule
 * written in JSON. `1500000n === 1500000` is false in JavaScript, and a promotion configured with
 * `order.total >= 1500000` that never fires against a bigint total is exactly the silent failure
 * this language is meant not to have.
 */
function sameValue(a, b) {
    if (typeof a === 'bigint' || typeof b === 'bigint') {
        if (typeof a === 'boolean' || typeof b === 'boolean')
            return false;
        if ((typeof a === 'bigint' || typeof a === 'number') &&
            (typeof b === 'bigint' || typeof b === 'number')) {
            // Loose on purpose, and only between the two numeric types.
            return BigInt(Math.trunc(Number(a))) === BigInt(Math.trunc(Number(b)));
        }
        return false;
    }
    if (Array.isArray(a) || Array.isArray(b))
        return false;
    return a === b;
}
function compareNumeric(a, b) {
    if (typeof a === 'bigint' || typeof b === 'bigint') {
        const left = typeof a === 'bigint' ? a : BigInt(Math.trunc(Number(a)));
        const right = typeof b === 'bigint' ? b : BigInt(Math.trunc(Number(b)));
        return left < right ? -1 : left > right ? 1 : 0;
    }
    const left = Number(a);
    const right = Number(b);
    return left < right ? -1 : left > right ? 1 : 0;
}
/**
 * `in` against a scalar asks "is it one of these"; against a list field it asks "does the list
 * contain any of these", which is the only useful reading for `line.treatment_ids`.
 */
function membership(actual, expected) {
    if (Array.isArray(actual))
        return actual.some((item) => expected.some((e) => sameValue(item, e)));
    return expected.some((e) => sameValue(actual, e));
}
/** Every field a condition touches, so a caller knows which facts to gather. */
export function fieldsUsed(condition) {
    const out = new Set();
    const walk = (node) => {
        if (!isRecord(node))
            return;
        for (const key of ['all', 'any', 'none']) {
            const children = node[key];
            if (Array.isArray(children))
                children.forEach(walk);
        }
        if (typeof node['field'] === 'string')
            out.add(node['field']);
    };
    walk(condition);
    return [...out].sort();
}
//# sourceMappingURL=condition.js.map